import type { ImageAIProvider, ProviderInfo, PollResult } from '../../types/providers';
import type { OperationType } from '../../types/operations';

interface FalModelConfig {
  endpoint: string; // e.g. "fal-ai/flux-pro/v1/fill"
  buildInput: (
    imageDataUri: string,
    options?: Record<string, unknown>
  ) => Record<string, unknown>;
  estimatedCost: number;
}

const MODELS: Partial<Record<OperationType, FalModelConfig>> = {
  outpaint: {
    endpoint: 'fal-ai/flux-pro/v1/fill',
    buildInput: (image, opts) => ({
      image_url: image,
      prompt: opts?.prompt || 'extend this image naturally, seamless continuation',
      num_inference_steps: 50,
      guidance_scale: 3.5,
    }),
    estimatedCost: 0.05,
  },
  inpaint: {
    endpoint: 'fal-ai/flux-pro/v1/fill',
    buildInput: (image, opts) => ({
      image_url: image,
      prompt: opts?.prompt || 'clean background, seamless',
      num_inference_steps: 50,
      guidance_scale: 3.5,
    }),
    estimatedCost: 0.05,
  },
};

async function blobToDataUri(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

interface FalStatusResponse {
  status: 'IN_QUEUE' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';
  error?: string;
  logs?: Array<{ message?: string }>;
}

interface FalResultResponse {
  images?: Array<{ url: string; content_type?: string; width?: number; height?: number }>;
  error?: string;
}

interface FalSubmitResponse {
  request_id: string;
  status_url?: string;
  response_url?: string;
}

export class FalProvider implements ImageAIProvider {
  readonly info: ProviderInfo = {
    id: 'fal',
    name: 'fal.ai',
    tier: 'paid',
    supportedOps: ['inpaint', 'outpaint'],
    requiresApiKey: true,
    apiKeyUrl: 'https://fal.ai/dashboard/keys',
  };

  private apiToken: string;
  private baseUrl = 'https://queue.fal.run';
  // Track request_id -> endpoint, because status/result URLs are per-model
  private endpointByRequestId = new Map<string, string>();

  constructor(apiToken: string) {
    this.apiToken = apiToken;
  }

  private get headers(): Record<string, string> {
    return {
      Authorization: `Key ${this.apiToken}`,
      'Content-Type': 'application/json',
    };
  }

  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/`, {
        method: 'GET',
        headers: { Authorization: `Key ${this.apiToken}` },
      });
      // Any HTTP response (2xx/4xx) means the network is reachable.
      return res.status >= 200 && res.status < 500;
    } catch {
      return false;
    }
  }

  async submit(
    operation: OperationType,
    inputImage: Blob,
    options?: Record<string, unknown>
  ): Promise<string> {
    const config = MODELS[operation];
    if (!config) throw new Error(`fal.ai does not support operation: ${operation}`);

    const imageDataUri = await blobToDataUri(inputImage);
    const input = config.buildInput(imageDataUri, options);

    if (options?.mask instanceof Blob) {
      const maskDataUri = await blobToDataUri(options.mask);
      (input as Record<string, unknown>).mask_url = maskDataUri;
    }

    const url = `${this.baseUrl}/${config.endpoint}`;
    const res = await fetch(url, {
      method: 'POST',
      headers: this.headers,
      body: JSON.stringify(input),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      const msg = (err as { detail?: string; error?: string }).detail
        || (err as { error?: string }).error
        || `fal.ai API error: ${res.status}`;
      throw new Error(msg);
    }

    const submission = await res.json() as FalSubmitResponse;
    this.endpointByRequestId.set(submission.request_id, config.endpoint);
    return submission.request_id;
  }

  async poll(predictionId: string): Promise<PollResult> {
    const endpoint = this.endpointByRequestId.get(predictionId);
    if (!endpoint) {
      return { status: 'failed', error: 'Unknown request id — endpoint mapping missing' };
    }

    const statusRes = await fetch(
      `${this.baseUrl}/${endpoint}/requests/${predictionId}/status`,
      { headers: { Authorization: `Key ${this.apiToken}` } }
    );

    if (!statusRes.ok) {
      return { status: 'failed', error: `Poll failed: ${statusRes.status}` };
    }

    const statusJson = await statusRes.json() as FalStatusResponse;

    switch (statusJson.status) {
      case 'IN_QUEUE':
      case 'IN_PROGRESS':
        return { status: 'processing' };

      case 'FAILED': {
        const logMsg = statusJson.logs?.map(l => l.message).filter(Boolean).join('; ');
        return {
          status: 'failed',
          error: statusJson.error || logMsg || 'fal.ai request failed',
        };
      }

      case 'COMPLETED': {
        const resultRes = await fetch(
          `${this.baseUrl}/${endpoint}/requests/${predictionId}`,
          { headers: { Authorization: `Key ${this.apiToken}` } }
        );
        if (!resultRes.ok) {
          return { status: 'failed', error: `Result fetch failed: ${resultRes.status}` };
        }
        const resultJson = await resultRes.json() as FalResultResponse;
        const url = resultJson.images?.[0]?.url;
        if (!url) {
          return { status: 'failed', error: resultJson.error || 'fal.ai returned no image url' };
        }
        return { status: 'succeeded', output: [url] };
      }

      default:
        return { status: 'processing' };
    }
  }

  async cancel(predictionId: string): Promise<void> {
    const endpoint = this.endpointByRequestId.get(predictionId);
    if (!endpoint) return;
    await fetch(
      `${this.baseUrl}/${endpoint}/requests/${predictionId}/cancel`,
      {
        method: 'PUT',
        headers: { Authorization: `Key ${this.apiToken}` },
      }
    ).catch(() => {});
  }

  getEstimatedCost(operation: OperationType): number {
    return MODELS[operation]?.estimatedCost ?? 0;
  }
}
